import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import { LOCALES } from '../i18n';
import { useLocale } from '../i18n/localeContext';
import { COLOR_MODES, type ColorMode } from '../theme/colorMode';
import { useColorMode } from '../theme/colorModeContext';
import {
    automaticReportsEnabled,
    setAutomaticReports
} from '../features/support/problemReport';
import { nativeShellVersion } from '../lib/nativeApp';

/**
 * The app's settings, reached from the account menu in both the office and the
 * field chrome: language, appearance and automatic problem reports.
 *
 * Every change applies at once and is remembered, so there is nothing to save:
 * the only action is Close.
 */
export default function SettingsDialog({
    open,
    onClose
}: {
    open: boolean;
    onClose: () => void;
}) {
    const intl = useIntl();
    const { locale, setLocale } = useLocale();
    const { mode, setMode } = useColorMode();
    const [reports, setReports] = useState(automaticReportsEnabled);
    const shellVersion = nativeShellVersion();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                <FormattedMessage id="settings.title" />
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <TextField
                        select
                        fullWidth
                        value={locale}
                        onChange={(event) => setLocale(event.target.value)}
                        label={intl.formatMessage({ id: 'layout.language' })}
                    >
                        {/* Languages are named in themselves, so the list reads
                            the same whichever one you are stuck in — which is
                            the point when you picked the wrong one. */}
                        {LOCALES.map((entry) => (
                            <MenuItem key={entry.code} value={entry.code}>
                                {entry.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        fullWidth
                        value={mode}
                        onChange={(event) =>
                            setMode(event.target.value as ColorMode)
                        }
                        label={intl.formatMessage({ id: 'settings.theme' })}
                    >
                        {COLOR_MODES.map((entry) => (
                            <MenuItem key={entry.value} value={entry.value}>
                                {intl.formatMessage({ id: entry.labelId })}
                            </MenuItem>
                        ))}
                    </TextField>
                    {/* The app reports a crash, a start-up that hangs or a
                        sync that keeps failing by itself. Switchable because it
                        sends a description of this device somewhere without
                        being asked; it stays on unless it is turned off. */}
                    <FormControlLabel
                        control={
                            <Switch
                                checked={reports}
                                onChange={(event) => {
                                    setAutomaticReports(event.target.checked);
                                    setReports(event.target.checked);
                                }}
                            />
                        }
                        label={
                            <Stack>
                                <Typography variant="body2">
                                    <FormattedMessage id="settings.reports" />
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ color: 'text.secondary' }}
                                >
                                    <FormattedMessage id="settings.reportsHint" />
                                </Typography>
                            </Stack>
                        }
                    />
                    {/* Build version, so testers can name what they are looking
                        at — and in the Android app the shell's own. */}
                    <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary' }}
                    >
                        <FormattedMessage
                            id="settings.version"
                            values={{ version: __APP_VERSION__ }}
                        />
                    </Typography>
                    {shellVersion && (
                        <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary' }}
                        >
                            <FormattedMessage
                                id="settings.shellVersion"
                                values={{ version: shellVersion }}
                            />
                        </Typography>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose}>
                    <FormattedMessage id="common.close" />
                </Button>
            </DialogActions>
        </Dialog>
    );
}
